import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Send, Filter, CreditCard, Lock, Unlock, CalendarClock, Search } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import MessageFiles from '../../components/MessageFiles';
import LessonFiles from '../../components/LessonFiles';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import PasswordInput from '../../components/PasswordInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function PaymentsTab({ ctx }) {
  const {
    token,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  } = ctx;
  return (
          <TabsContent value="payments" data-testid="payments-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Управление на плащания</h2>
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="update-payment-btn"
                      className="bg-[#00966E] hover:bg-[#007a59] text-white flex items-center gap-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      Обнови плащане
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Обновяване на плащане</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdatePayment} className="space-y-4">
                      <div>
                        <Label htmlFor="paymentStudent">Ученик</Label>
                        <select
                          id="paymentStudent"
                          data-testid="payment-student-select"
                          value={paymentStudent}
                          onChange={(e) => setPaymentStudent(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-md mt-1"
                          required
                        >
                          <option value="">Изберете ученик</option>
                          {students.map((student) => (
                            <option key={student.user_id} value={student.user_id}>
                              {getFullName(student)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="month">Месец</Label>
                          <select
                            id="month"
                            data-testid="payment-month-select"
                            value={paymentMonth}
                            onChange={(e) => setPaymentMonth(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-md mt-1"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                              <option key={m} value={m}>
                                {new Date(2024, m - 1).toLocaleDateString('bg-BG', { month: 'long' })}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="year">Година</Label>
                          <Input
                            id="year"
                            data-testid="payment-year-input"
                            type="number"
                            value={paymentYear}
                            onChange={(e) => setPaymentYear(e.target.value)}
                            className="mt-1"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="status">Статус</Label>
                        <select
                          id="status"
                          data-testid="payment-status-select"
                          value={paymentStatus}
                          onChange={(e) => setPaymentStatus(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-md mt-1"
                        >
                          <option value="paid">Платено</option>
                          <option value="unpaid">Неплатено</option>
                        </select>
                      </div>
                      <Button type="submit" data-testid="submit-payment-btn" className="w-full bg-[#00966E] hover:bg-[#007a59] text-white">
                        Запази
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Payment records table */}
              {payments.length === 0 ? (
                <Card className="p-8 text-center text-slate-500">
                  <CreditCard className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>Няма записани плащания</p>
                  <p className="text-sm mt-1">Използвайте бутона "Обнови плащане" за да добавите запис.</p>
                </Card>
              ) : (
                <div className="rounded-md border border-slate-200 overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Ученик</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Месец</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Година</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Статус</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Обновено</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => {
                        const student = students.find(s => s.user_id === payment.student_id);
                        const monthName = new Date(2024, payment.month - 1).toLocaleDateString('bg-BG', { month: 'long' });
                        return (
                          <tr key={payment.payment_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">
                              {student ? getFullName(student) : payment.student_id}
                              {student && <div className="text-xs text-slate-400">@{student.username}</div>}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 capitalize">{monthName}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{payment.year}</td>
                            <td className="px-4 py-3">
                              {payment.status === 'paid' ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">Платена Такса</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 border-red-200">Неплатена Такса</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400">
                              {new Date(payment.updated_at).toLocaleDateString('bg-BG')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
  );
}
